import {
  type DefaultError,
  type MutationObserverOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/vue-query';
import { computed, MaybeRefOrGetter, toValue } from 'vue';

import { VolumeDiscount, VolumeDiscountApplied, VolumeDiscountNew } from '@/services/api/models/pricing';
import {
  createVolumeDiscount,
  getVolumeDiscountById,
  getVolumeDiscounts,
  getVolumeDiscountsForBrands,
  getVolumeDiscountsForOrder,
  removeVolumeDiscount,
  updateVolumeDiscount,
  updateVolumeDiscountStatus,
} from '@/services/api/pricing';

// The query key factory pattern comes from: https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
export const pricingQueryKeys = {
  volumeDiscounts: ['pricing', 'volumeDiscounts'] as const,
  volumeDiscountsById: (id: MaybeRefOrGetter<string>) => [...pricingQueryKeys.volumeDiscounts, id] as const,
  volumeDiscountsForBrands: () => [...pricingQueryKeys.volumeDiscounts, 'brands'] as const,
  volumeDiscountsByBrandId: (brandId: MaybeRefOrGetter<string>) => {
    return [...pricingQueryKeys.volumeDiscountsForBrands(), brandId] as const;
  },
  appliedVolumeDiscountsByOrderId: (orderId: MaybeRefOrGetter<string>) => {
    return [pricingQueryKeys.volumeDiscounts, 'applied', orderId] as const;
  },
};

type UseVolumeDiscountsQueryArgs = Partial<UseQueryOptions<VolumeDiscount[]>>;

export const useVolumeDiscountsQuery = (options?: UseVolumeDiscountsQueryArgs) => {
  return useQuery({
    queryKey: pricingQueryKeys.volumeDiscounts,
    queryFn: getVolumeDiscounts,
    ...options,
  });
};

type UseVolumeDiscountsByIdQueryArgs = Partial<UseQueryOptions<VolumeDiscount>> & {
  volumeDiscountId: MaybeRefOrGetter<string>;
};

export const useVolumeDiscountsByIdQuery = ({ volumeDiscountId, ...options }: UseVolumeDiscountsByIdQueryArgs) => {
  return useQuery({
    queryKey: pricingQueryKeys.volumeDiscountsById(volumeDiscountId),
    queryFn: () => getVolumeDiscountById(Number(toValue(volumeDiscountId))),
    enabled: computed(() => !!Number(toValue(volumeDiscountId))),
    ...options,
  });
};

type UseVolumeDiscountsForBrandsQueryArgs = Partial<UseQueryOptions<VolumeDiscount[]>>;

export const useVolumeDiscountsForBrandsQuery = (options?: UseVolumeDiscountsForBrandsQueryArgs) => {
  return useQuery({
    queryKey: pricingQueryKeys.volumeDiscountsForBrands(),
    queryFn: () => getVolumeDiscountsForBrands(),
    ...options,
  });
};

type UseVolumeDiscountsByBrandIdQueryArgs = Partial<UseQueryOptions<VolumeDiscount[]>> & {
  brandId: MaybeRefOrGetter<string>;
};

export const useVolumeDiscountsByBrandIdQuery = ({ brandId, ...options }: UseVolumeDiscountsByBrandIdQueryArgs) => {
  return useQuery({
    queryKey: pricingQueryKeys.volumeDiscountsByBrandId(brandId),
    queryFn: () => getVolumeDiscountsForBrands(Number(toValue(brandId))),
    enabled: computed(() => !!Number(toValue(brandId))),
    ...options,
  });
};

type UseAppliedVolumeDiscountsForOrderQueryArgs = Partial<UseQueryOptions<VolumeDiscountApplied[]>> & {
  orderId: MaybeRefOrGetter<string>;
};

export const useAppliedVolumeDiscountsForOrderQuery = ({
  orderId,
  ...options
}: UseAppliedVolumeDiscountsForOrderQueryArgs) => {
  return useQuery({
    queryKey: pricingQueryKeys.appliedVolumeDiscountsByOrderId(orderId),
    queryFn: () => getVolumeDiscountsForOrder(toValue(orderId)),
    enabled: computed(() => !!toValue(orderId)),
    ...options,
  });
};

type UseCreateVolumeDiscountMutationArgs = Partial<
  MutationObserverOptions<VolumeDiscount, DefaultError, { discount: VolumeDiscountNew }>
>;

export function useCreateVolumeDiscountMutation(options?: UseCreateVolumeDiscountMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ discount }) => createVolumeDiscount(discount),
    ...options,
    onSuccess: (...args) => {
      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscounts, exact: true });

      options?.onSuccess?.(...args);
    },
  });
}

type UseUpdateVolumeDiscountMutationArgs = Partial<
  MutationObserverOptions<VolumeDiscount, DefaultError, { discount: VolumeDiscount }>
>;

export function useUpdateVolumeDiscountMutation(options?: UseUpdateVolumeDiscountMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ discount }) => updateVolumeDiscount(discount),
    ...options,
    onSuccess: (...args) => {
      const { id } = args[0];

      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscounts, exact: true });
      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscountsById(String(id)) });

      options?.onSuccess?.(...args);
    },
  });
}

type UseDeleteVolumeDiscountMutationArgs = Partial<
  MutationObserverOptions<{ id: number }, DefaultError, { id: number }>
>;

export function useDeleteVolumeDiscountMutation(options?: UseDeleteVolumeDiscountMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeVolumeDiscount,
    ...options,
    onSuccess: (...args) => {
      const { id } = args[0];

      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscounts, exact: true });
      queryClient.removeQueries({ queryKey: pricingQueryKeys.volumeDiscountsById(String(id)) });

      options?.onSuccess?.(...args);
    },
  });
}

type UseUpdateVolumeDiscountStatusMutationArgs = Partial<
  MutationObserverOptions<{ id: number }, DefaultError, { id: number; status: string }>
>;

export function useUpdateVolumeDiscountStatusMutation(options?: UseUpdateVolumeDiscountStatusMutationArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateVolumeDiscountStatus,
    ...options,
    onSuccess: (...args) => {
      const { id } = args[0];

      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscounts, exact: true });
      queryClient.refetchQueries({ queryKey: pricingQueryKeys.volumeDiscountsById(String(id)) });

      options?.onSuccess?.(...args);
    },
  });
}
